import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Header from "../../components/header";
import Footer from "../../components/footer";
import saveImage from "./assets/save.jpg";
import targetImage from "./assets/target.jpg";
import kidsImage from "./assets/kids.jpg";
import esusuImage from "./assets/esusu.jpg";
import heroPattern from "../../images/current-account/hero-pattern.svg";
import checkIcon from "../../images/current-account/check.svg";
import styles from "./index.module.css";

const accountUrl = "https://zikoramfb.qoreonline.com/onboarding/type";
const appUrl = "https://onelink.to/zikora";

const products = [
  {
    id: "save",
    shortTitle: "Save",
    title: "Zikora MFB Save",
    image: saveImage,
    imageAlt: "Seedlings growing on ascending blocks",
    description: "Save towards your future starting from today. Zikora MFB Save is designed to hand hold the saving beginners.",
    benefits: [
      "Earn Interest On Your Savings",
      "Access to 24/7, All year round banking services.",
      "Access to Zikora MFB’s Digital Banking Suite.",
      "Access to personalised debit cards for worldwide online transactions.",
    ],
    requirements: [
      "Signed copy of Zikora MFB account opening form",
      "Valid identification (Passport, Driving license etc)",
      "One passport photograph",
      "3 Months utility bill with current address",
    ],
  },
  {
    id: "target",
    shortTitle: "Target",
    title: "Zikora MFB Target",
    image: targetImage,
    imageAlt: "Green staircase with a gold ball",
    description: "Save toward a project or a set goal over a period of time. This account remains locked for the selected time in order to help you reach your goal.",
    benefits: [
      "Earn Interest On Your Savings",
      "Make deposits on a daily, weekly, monthly, quarterly or yearly basis.",
      "Make deposits through branch deposits or the Digital Suite",
      "Open this account through the internet banking or at any of our branches.",
    ],
    requirements: [
      "Minimum opening balance of N1,000",
      "Signed copy of Zikora MFB account opening form",
      "Valid identification (Passport, Driving license etc)",
      "One passport photograph",
      "3 Months utility bill with current address",
    ],
  },
  {
    id: "kids",
    shortTitle: "For Kids",
    title: "Zikora MFB For Kids",
    image: kidsImage,
    imageAlt: "Mother and child building with wooden blocks",
    description: "Give your children a head start in life. This account will enable you save for school fees, child expenses and will help imbibe the saving habit in your child.",
    benefits: [
      "Account allows large lodgements toward future expenses for the child.",
      "Interest earned on savings.",
      "Ownership of the account can be transferred to the child when the child is 18.",
    ],
    requirements: [
      "Minimum opening balance of N1,000",
      "Signed copy of Zikora MFB account opening form",
      "Valid identification (Passport, Driving license etc)",
      "Copy of child's birth certificate or age declaration",
      "One passport photograph",
      "3 Months utility bill with current address",
    ],
  },
  {
    id: "esusu",
    shortTitle: "Esusu",
    title: "Zikora MFB Esusu",
    image: esusuImage,
    imageAlt: "A community placing their hands together",
    description: "Save regularly with your friends and community. Each member of your group contributes a set amount monthly and receives a pre-assigned slot in which they can access the funds. This will be automated via our Digital Banking Suite.",
    benefits: [
      "Each group has a maximum of 12 contributors.",
      "Participants will receive bulk payments once their slot has arrived.",
    ],
    requirements: [
      "All contributors must have an operational Zikora MFB Account.",
      "Signed copy of the Zikora MFB Esusu account opening form.",
    ],
  },
];

const Label = ({ children, light = false }) => (
  <div className={styles.label + (light ? " " + styles.labelLight : "")}>
    <span aria-hidden="true" />{children}
  </div>
);

const Actions = ({ compact = false }) => (
  <div className={styles.actions + (compact ? " " + styles.compactActions : "")}>
    <a className={styles.primaryButton} href={accountUrl} target="_blank" rel="noopener noreferrer">Open Account</a>
    <a className={styles.secondaryButton} href={appUrl} target="_blank" rel="noopener noreferrer">Download the App</a>
  </div>
);

const RequirementList = ({ requirements }) => {
  const split = Math.ceil(requirements.length / 2);
  return <div className={styles.requirements}>
    <div className={styles.requirementsLabel}>Requirements</div>
    <div className={styles.requirementColumns}>
      {[requirements.slice(0, split), requirements.slice(split)].map((column, columnIndex) => (
        <ul key={columnIndex}>{column.map((item) => <li key={item}><img src={checkIcon} alt="" />{item}</li>)}</ul>
      ))}
    </div>
  </div>;
};

const ProductArticle = ({ product, index, reducedMotion }) => <motion.article
  className={styles.accountArticle}
  id={product.id}
  initial={reducedMotion ? false : { opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true, amount: 0.12 }}
  transition={{ duration: reducedMotion ? 0 : 0.65, ease: [0.22, 1, 0.36, 1] }}
>
  <img className={styles.accountImage} src={product.image} alt={product.imageAlt} loading={index === 0 ? "eager" : "lazy"} decoding="async" />
  <div className={styles.accountDetails}>
    <div className={styles.accountIntro}>
      <div><span className={styles.accountNumber}>0{index + 1}</span><h2>{product.title}</h2></div>
      <p>{product.description}</p>
    </div>
    <ul className={styles.benefits}>{product.benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}</ul>
    <RequirementList requirements={product.requirements} />
  </div>
</motion.article>;

const SavingsProducts = () => {
  const [activeProduct, setActiveProduct] = useState(0);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const sections = products.map(({ id }) => document.getElementById(id)).filter(Boolean);
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      if (visible.length) setActiveProduct(products.findIndex(({ id }) => id === visible[0].target.id));
    }, { rootMargin: "-15% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75] });
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return <main className={styles.page}>
    <Header solid compactMobile />
    <section className={styles.hero} style={{ "--hero-pattern": "url(" + heroPattern + ")" }}>
      <div className={styles.heroContent}>
        <Label light>Accounts</Label>
        <h1>Savings <em>Account</em></h1>
        <p>Zikora MFB has designed different types of savings accounts to help you spend and save according to your liking.</p>
        <Actions />
      </div>
    </section>

    <section className={styles.accountsSection} aria-label="Four ways to save">
      <div className={styles.accountsLayout}>
        <aside className={styles.accountIndex}>
          <Label>Four ways to save</Label>
          <p>Every account opens in the branch, on internet banking, or in the Zikora MFB App.</p>
          <nav aria-label="Savings account types">
            {products.map((product, index) => <a href={"#" + product.id} key={product.id} className={index === activeProduct ? styles.activeIndex : ""} aria-current={index === activeProduct ? "location" : undefined}>
              <span>0{index + 1}</span>{product.shortTitle}
            </a>)}
          </nav>
        </aside>
        <div className={styles.accountList}>
          <div className={styles.mobileSectionLabel}>
            <Label>Four ways to save</Label>
            <p>Every account opens in the branch, on internet banking, or in the Zikora MFB App.</p>
          </div>
          {products.map((product, index) => <ProductArticle key={product.id} product={product} index={index} reducedMotion={reducedMotion} />)}
        </div>
      </div>
    </section>

    <section className={styles.ctaSection}>
      <div className={styles.ctaCard}>
        <Label light>Get started</Label>
        <h2>Ready to get started with smarter banking?</h2>
        <p>Open your account in minutes or download the Zikora App to bank anytime, anywhere.</p>
        <Actions compact />
      </div>
    </section>
    <div className={styles.footer}><Footer about /></div>
  </main>;
};

export default SavingsProducts;
